import styled from 'styled-components';
import React, { SyntheticEvent, useEffect, useState } from 'react';

import { LicencePlate } from '../licencePlate';
import { LicencePlateButton } from '../licensePlate/licensePlateButton';
import { Modal } from '../modal';
import { useHasAccess } from '../../../hooks/useHasAccess';
import { useHasViewPermission } from '../../../hooks/useHasPermission';
import { useKeyHolder } from '../../../hooks/useKeyHolder';
import { useToggle } from '../../../hooks/useToggle';
import { Event } from '../../../interfaces';
import {
  getHasLicensePlateImage,
  getHasLicensePlateText,
  humanizeEnum,
  isEventVideoRemoved,
  selectEventThumbnailUrl,
} from '../../../utils';
import { EventVideo } from '../../event_details/EventVideo';
import { InternalOnly } from '../adminOnly';
import { CanvasWrapper, ZoomableImage } from '../imagesWithOverlays/zoomableImage';
import { ImageWithSvgOverlays } from '../imagesWithOverlays/imageWithSvgOverlays';
import {
  ButtonEventView,
  Description,
  EventVideoThumbnailWrapper,
  GridEventLPRWrapper,
  LPButtonContainer,
  LicensePlateWrapper,
  StyledEventVideo,
  UploadStatus,
} from './atoms';
import { getMediaStatus } from './getMediaStatus';
import { StateSwitcherButton } from './stateSwitcherButton';
import { useMetadataControls } from './useMetadataControls';
import { PreImageThumb } from '../../reviewGridMatchedEvents/PreImageThumb';
import moment from 'moment';

interface Props {
  event: Event;
  className?: string;
  description?: string;
  width?: string;
  height?: string;
  zoomableImage?: boolean;
  onClick?: (evt: any) => void;
  eager?: boolean;
  preloadVideo?: boolean;
  drawMetadata?: boolean;
  drawLprPov?: boolean;
  'data-accesskey'?: string | number;
  hidePublishedBadge?: boolean;
  allowInlineVideo?: boolean;
  isStandardImageStyle?: boolean;
  buttonViewClick?: (evt: any) => void;
  showLicensePlates?: boolean;
  licensePlateUpdate?: (eventPartial: Partial<Event>) => void;
  thumbState?: string;
  licensePlateTargetWidth?: number;
  noMargin?: boolean;
  withModal?: boolean;
  enableLicensePlates?: boolean;
  showPreImage?: boolean;
  useBiggerLicensePlateText?: boolean;
}

export function EventThumb(props: Props) {
  const [modalVisible, toggleModal] = useToggle(false);
  const hasLicensePlateImage = getHasLicensePlateImage(props.event);
  const {
    event,
    description,
    eager,
    onClick,
    width,
    height,
    buttonViewClick,
    showLicensePlates,
    licensePlateUpdate,
    zoomableImage,
    allowInlineVideo,
    thumbState,
    licensePlateTargetWidth,
    className,
    noMargin,
    withModal = true,
    enableLicensePlates = hasLicensePlateImage,
    useBiggerLicensePlateText,
  } = props;

  const { getImageWithMetadataParameters, MetadataControls } = useMetadataControls(event);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [isVideo, setVideo] = useState<boolean>(props.preloadVideo ?? false);
  const canView = useHasViewPermission('events:read:resource');
  const hasAccess = useHasAccess();

  useEffect(() => {
    setVideo(props.preloadVideo ?? false);
    setThumbnail(selectEventThumbnailUrl(props.event, hasAccess));
  }, [event?.id]);

  const mediaStatus = getMediaStatus(props.event);

  const togglePlay = (evt: SyntheticEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    setVideo(!isVideo);
  };

  const basicKey = '`';

  const basicKeyHeld = useKeyHolder(basicKey);

  if (!canView) {
    return null;
  }
  if (isEventVideoRemoved(event)) {
    return null;
  }

  return (
    <>
      <EventVideoThumbnailWrapper
        isVideo={isVideo}
        thumbState={thumbState}
        onClick={allowInlineVideo ? togglePlay : toggleModal}
        data-accesskey={basicKeyHeld ? undefined : props['data-accesskey']}
        className={className}
        noMargin={noMargin}
        data-test="event-thumb"
        role="button"
        tabIndex={0}
      >
        {isVideo ? (
          <StyledEventVideo event={event} preloadVideo={props.preloadVideo} />
        ) : (
          <ImageWithSvgOverlays
            src={thumbnail}
            event={event}
            loading={eager ? 'eager' : 'lazy'}
            width={width}
            height={height}
            drawLprPov={props.drawLprPov ?? props.drawMetadata}
            ariaLabel={`Violation preview for ${event.license_plate} ${moment(event.captured_at).fromNow()}`}
          />
        )}

        {description && <Description>{description}</Description>}

        <InternalOnly>{!props.hidePublishedBadge && <UploadStatus>{humanizeEnum(mediaStatus)}</UploadStatus>}</InternalOnly>

        {/* show mediaSwitcher if allowInlineVideo and there are videos and the first video IS FULLY published */}
        {allowInlineVideo && (
          <StateSwitcherButton
            onClick={onClick}
            thumbState={thumbState}
            data-accesskey={basicKeyHeld ? props['data-accesskey'] : undefined}
            data-test="event-thumb-state-switcher"
          />
        )}
        {buttonViewClick && <ButtonEventView onClick={buttonViewClick}>View</ButtonEventView>}

        {enableLicensePlates && (
          <GridEventLPRWrapper showLicensePlates={showLicensePlates}>
            {getHasLicensePlateImage(event) && (
              <LPButtonContainer>
                <LicencePlateButton event={event} size="sm" licensePlateUpdate={licensePlateUpdate} isBigger={useBiggerLicensePlateText} />
              </LPButtonContainer>
            )}
            {getHasLicensePlateText(event) && (
              <LicensePlateWrapper>
                <LicencePlate event={event} targetWidth={licensePlateTargetWidth || 120} showRaw="hover" />
              </LicensePlateWrapper>
            )}
          </GridEventLPRWrapper>
        )}

        {props.showPreImage && !isVideo && <PreImageThumb event={event} />}
      </EventVideoThumbnailWrapper>

      {withModal && (
        <Modal isOpen={modalVisible} onToggle={toggleModal} contentLabel="Violation preview">
          <Wrapper>
            {zoomableImage ? (
              <ZoomableImage
                src={thumbnail}
                event={event}
                {...getImageWithMetadataParameters()}
                ariaLabel={`Violation preview for ${event.license_plate} ${moment(event.captured_at).fromNow()}`}
              />
            ) : (
              <EventVideo event={event} />
            )}

            <InternalOnly>
              <MetadataControls />
            </InternalOnly>
          </Wrapper>
        </Modal>
      )}
    </>
  );
}

const Wrapper = styled.div`
  height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2em;

  ${CanvasWrapper} {
    width: 100%;
  }
`;
